/**
 * jp_attribution cookie reader (gdn-1180).
 *
 * The marketing site sets `jp_attribution` on every page load on .jotpsych.com
 * with UTMs, click IDs, referrer, and landing path. We read it once on Auth0
 * callback so the BE can persist it as the user's first-touch attribution.
 *
 * Cookie schema documented in interstitial-engineering-asks-2026-04-27.md.
 */

const COOKIE_NAME = 'jp_attribution';

export type JpAttributionCookie = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;

  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;

  referrer?: string;
  landing_path?: string;
  first_seen_ts?: number;
  last_seen_ts?: number;
};

/**
 * Returns the parsed jp_attribution cookie, or null if missing/malformed.
 *
 * Never throws — private browsing, ad-blockers, malformed JSON, and "no cookie
 * set yet" all collapse into a null return so callers can fall through to
 * direct/none attribution without error handling.
 */
export function readJpAttributionCookie(): JpAttributionCookie | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const prefix = `${COOKIE_NAME}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed.startsWith(prefix)) continue;

    const raw = trimmed.slice(prefix.length);
    if (!raw) return null;

    try {
      const decoded = decodeURIComponent(raw);
      const parsed: unknown = JSON.parse(decoded);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }
  return null;
}
